/**
 * Exercise Service
 *
 * Service for managing exercise operations including CRUD operations,
 * and filtering. Extends the base
 * CrudService to provide exercise-specific functionality with proper
 * error handling.
 *
 * @module services/exercise
 */

import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';

import type { QueryParams } from '../types/api/query.types';
import type { ID } from '../types/common/id.types';
import type {
  Exercise,
  ExerciseDetail,
  FavoriteExercise,
} from '../types/domain/exercise.types';

// =============================================================================
// SPECIALIZED SERVICES
// =============================================================================

/**
 * FavoriteExerciseService - Manages user's favorite exercises
 * Handles adding, removing, and retrieving user's favorite exercises
 */
class FavoriteExerciseService extends CrudService<FavoriteExercise> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/favourite',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  /**
   * Add exercise to favorites
   */
  public async addFavorite(guid: ID): Promise<void> {
    try {
      await this.create({ guid: String(guid) });
    } catch (error) {
      console.error(`Failed to add exercise ${guid} to favorites:`, error);
      rethrowTyped(
        error,
        'Unable to add exercise to favorites. Please try again.'
      );
    }
  }

  /**
   * Remove exercise from favorites
   */
  public async removeFavorite(guid: ID): Promise<void> {
    try {
      await this.delete(guid);
    } catch (error) {
      console.error(`Failed to remove exercise ${guid} from favorites:`, error);
      rethrowTyped(
        error,
        'Unable to remove exercise from favorites. Please try again.'
      );
    }
  }
}

// =============================================================================
// COMBINED EXERCISE SERVICE CLASS
// =============================================================================

/**
 * ExerciseService - Handles exercise database operations
 * Manages exercises with full CRUD capabilities
 *
 * List and detail are different shapes: `getAll` yields the lightweight
 * {@link Exercise}, while `getById` yields the richer {@link ExerciseDetail}
 * that the exercise view page renders.
 */
export class ExerciseService extends CrudService<
  ExerciseDetail,
  Exercise,
  Exercise
> {
  private readonly favoriteService: FavoriteExerciseService;

  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/exercise',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
      // The detail carries the viewer's stats, records and history in the
      // viewer's unit, so a unit switch must miss the cached entry.
      cacheVariesByUnit: true,
    });

    this.favoriteService = new FavoriteExerciseService(cacheTTL);
  }

  /**
   * Search exercises with advanced filtering
   *
   * @param params - Search and filter parameters
   * @returns Promise resolving exercise list
   */
  public override async getAll(params: QueryParams): Promise<Exercise[]> {
    try {
      return await super.getAll(params);
    } catch (error) {
      console.error('Failed to search exercises:', error);
      rethrowTyped(error, 'Unable to search exercises. Please try again.');
    }
  }

  /**
   * Fetch the full detail payload for a single exercise
   *
   * @param id - Exercise identifier
   * @returns Promise resolving to exercise details
   */
  public override async getById(id: ID): Promise<ExerciseDetail> {
    if (!id) {
      throw new Error('Exercise ID is required');
    }

    try {
      // CrudService does not apply transformResponse itself — each service
      // invokes it from its own read path (see workoutService/planService).
      const exercise = await super.getById(id);
      return this.transformResponse(exercise);
    } catch (error) {
      console.error(`Failed to fetch exercise ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to fetch exercise details. Please check the exercise ID and try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // ADVANCED EXERCISE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Adds an exercise to user's favorites
   * The exercise can be from any source (external database or custom exercises).
   *
   * @param id - Exercise identifier to add to favorites
   * @returns Promise resolving when favorite is successfully added
   */
  public async addToFavorites(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Exercise ID is required');
    }
    return this.favoriteService.addFavorite(id);
  }

  /**
   * Removes an exercise from user's favorites
   * This action is reversible - the exercise can be re-added later.
   *
   * @param id - Exercise identifier to remove from favorites
   * @returns Promise resolving when favorite is successfully removed
   */
  public async removeFromFavorites(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Exercise ID is required');
    }
    return this.favoriteService.removeFavorite(id);
  }

  /**
   * Retrieves user's favorite exercises list
   *
   * @returns Promise resolving favorite exercise list
   */
  public async getFavorites(): Promise<FavoriteExercise[]> {
    try {
      return await this.favoriteService.getAll();
    } catch (error) {
      console.error('Failed to fetch favorite exercises:', error);
      rethrowTyped(
        error,
        'Unable to fetch favorite exercises. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // CACHE MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Clear all caches across specialized services
   * Useful for forcing fresh data retrieval or after significant data changes
   */
  public clearAllCaches(): void {
    this.clearCache();
    this.favoriteService.clearCache();
  }

  /**
   * Enable/disable caching across all specialized services
   * Allows runtime control of caching behavior for all exercise operations
   */
  public override setCacheEnabled(enabled: boolean): void {
    this.favoriteService.setCacheEnabled(enabled);
  }

  // ---------------------------------------------------------------------------
  // TRANSFORMATION METHOD
  // ---------------------------------------------------------------------------

  /**
   * Transform response data received from API
   * Formats data for frontend consumption
   *
   * @param data - Raw API response data
   * @returns Transformed response data
   */
  protected override transformResponse(data: ExerciseDetail): ExerciseDetail {
    // Collections default to empty so the page never has to null-check them;
    // the analytic blocks stay null, since "never trained" is a real state the
    // page renders differently rather than an absence to paper over.
    return {
      ...data,
      secondary_muscles: data.secondary_muscles ?? [],
      instructions: data.instructions ?? [],
      records: data.records ?? [],
      history: data.history ?? [],
      trained_in: data.trained_in ?? [],
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default exercise service instance
 * Pre-configured and ready to use throughout the application
 */
export const exerciseService = new ExerciseService();

/**
 * Default favorite exercise service instance
 * Pre-configured and ready to use throughout the application
 */
export const favoriteExerciseService = new FavoriteExerciseService();
